// src/pages/IssueListPage.tsx — SCE Issues
// Tous les problèmes : un tableau pleine largeur au bureau, des rangées denses au
// téléphone. Colonnes au choix, tri, filtres sur les en-têtes, regroupement
// (machine par défaut) — tout est mémorisé par utilisateur (useReglagesTableau).
//
// Les problèmes sont chargés UNE fois, sans filtre serveur : filtres, tri et
// regroupement se font en mémoire, donc instantanément et sans requête par clic
// (les colonnes de N° de série, OF et PN ne sont de toute façon pas indexées).
//
// « Mes déclarations » et « Mon périmètre » sont des vues prêtes à l'emploi, plus
// des cases cachées dans une fenêtre de filtres. Les annulés ne reviennent qu'en
// cochant « Annulé » dans le filtre Statut.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { Header } from '@/components/layout/Header'
import { SuiteListe } from '@/components/layout/SuiteProblemes'
import { RechercheEntete } from '@/components/accueil/RechercheGlobale'
import { BoutonRafraichirProblemes } from '@/components/ui/BoutonRafraichir'
import { ListeProblemesTelephone } from '@/components/issues/ListeProblemesTelephone'
import { BarreTableau } from '@/components/issues/tableau/BarreTableau'
import { FeuilleFiltres } from '@/components/issues/tableau/FeuilleFiltres'
import { PopoverFiltre } from '@/components/issues/tableau/PopoverFiltre'
import { TableauProblemes } from '@/components/issues/tableau/TableauProblemes'
import { PastilleMetier } from '@/components/issues/tableau/valeurs'
import { listAllEquipements } from '@/api/equipements.api'
import { getMetiersCoordonnes, pilote } from '@/config/coordinateurs.config'
import { SUJETS_CONFIG, rangeeMetiersEtSujets, sujetsDuResponsable } from '@/config/sujets.config'
import { estAdmin } from '@/config/admin.config'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { useIssues } from '@/hooks/useIssues'
import { useMaintenant } from '@/hooks/useMaintenant'
import { useReglagesTableau } from '@/hooks/useReglagesTableau'
import { useElementsSuivis } from '@/hooks/useElementsSuivis'
import { useIdSharePoint } from '@/hooks/useIdSharePoint'
import { estAbonne } from '@/utils/abonnements'
import { motifSuivi } from '@/utils/elementsSuivis'
import { SUJETS, cleRapprochement, compteCommeProbleme, type Equipement, type Issue } from '@/types/issue.types'
import { identitesDeclarant } from '@/utils/identiteDeclarant'
import { LIBELLE_SUITE_GLOBALE } from '@/utils/suiteProblemes'
import {
  appliquerVue,
  enregistrerVue,
  supprimerVue,
  vuesPersonnelles,
  cleDeGroupe,
  compterVue,
  filtrer,
  fusionnerSoeurs,
  basculerValeur,
  nbFiltresActifs,
  regrouper,
  resumerGroupe,
  trier,
  vueActive,
  vuesPredefinies,
  type ColonneId,
  type ContexteTableau,
  type EntreeTableau,
  type RangeeFusionnee,
  type Regroupement,
  type VuePredefinie,
} from '@/utils/tableauProblemes'
import { Confirmation } from '@/components/ui/Confirmation'
import { useSnackbar } from '@/components/ui/Snackbar'
import { useEpinglerAccueil } from '@/hooks/useDispositionAccueil'

const MODELE_INCONNU = 'Non identifié'

export function IssueListPage(): JSX.Element {
  const navigate = useNavigate()
  const estBureau = useEstBureau()
  const maintenant = useMaintenant()
  const { user, isFournisseur } = useCurrentUser()
  const interne = !isFournisseur

  const { issues: brutes, loading, error } = useIssues()
  const { reglages, modifier } = useReglagesTableau()
  // « Épingler à l'accueil » une vue personnelle (accueil personnalisable, lot 5 du 03/11).
  const epinglerAccueil = useEpinglerAccueil()
  const snackbar = useSnackbar()
  const [recherche, setRecherche] = useState('')
  // Le × d'une vue personnelle : confirmé dans l'appli, plus par la fenêtre du navigateur (02/11).
  const [vueASupprimer, setVueASupprimer] = useState<VuePredefinie | null>(null)
  const [repliees, setRepliees] = useState<ReadonlySet<string>>(new Set())
  const [filtre, setFiltre] = useState<{ col: ColonneId; ancre: HTMLElement } | null>(null)
  const [feuilleOuverte, setFeuilleOuverte] = useState(false)

  // Les groupes repliés n'ont de sens que pour le regroupement en cours.
  useEffect(() => setRepliees(new Set()), [reglages.regroupement])

  // ─── Référentiel : le modèle de chaque machine ───
  // Un fournisseur ne lit pas le référentiel (PN, CVE, chefs de projet de tous
  // les clients) : pas de colonne Modèle pour lui, donc pas de lecture.
  const [equipements, setEquipements] = useState<Equipement[]>([])
  useEffect(() => {
    if (!interne) return
    let annule = false
    listAllEquipements()
      .then((liste) => {
        if (!annule) setEquipements(liste)
      })
      .catch(() => undefined) // sans référentiel, le modèle reste « Non identifié »
    return () => {
      annule = true
    }
  }, [interne])

  const modeleParSerie = useMemo(
    () => new Map(equipements.map((e) => [cleRapprochement(e.numeroSerie), e.familleEquipement] as const)),
    [equipements],
  )
  const mesIdentites = useMemo(() => (user && interne ? identitesDeclarant(user) : null), [user, interne])
  // Métiers pilotés et sujets dont on est responsable (Thibaut : ELEC + AUTOM + GOULOTTE).
  // Au bureau, la page ne défile pas : seul le tableau défile (cf. .page-tableau).
  useEffect(() => {
    if (!estBureau) return
    document.documentElement.classList.add('page-tableau')
    return () => document.documentElement.classList.remove('page-tableau')
  }, [estBureau])

  const metiersPerimetre = useMemo(
    () => (user && interne ? rangeeMetiersEtSujets(getMetiersCoordonnes(user.upn), sujetsDuResponsable(user.upn)) : []),
    [user, interne],
  )
  // Vue « Goulottes » : pour qui les traite (pilotes Méca), leurs responsables et l'administrateur.
  const sujetsVues = useMemo(
    () =>
      user && interne
        ? SUJETS.filter((x) => estAdmin(user.upn) || pilote(user.upn, SUJETS_CONFIG[x].metier) || sujetsDuResponsable(user.upn).includes(x))
        : [],
    [user, interne],
  )

  // « Suivis » : exactement la règle d'« Éléments suivis » à l'accueil — la cloche
  // du problème, ou un équipement / projet suivi (problèmes actifs seulement).
  // Un fournisseur ne suit rien : ni identifiant ni listes à lire.
  const upnSuivi = interne ? user?.upn : undefined
  const monIdSharePoint = useIdSharePoint(upnSuivi)
  const { elements: elementsSuivis } = useElementsSuivis(upnSuivi)

  const ctx = useMemo<ContexteTableau>(
    () => ({
      modeleDe: (i: Issue) => modeleParSerie.get(cleRapprochement(i.numeroSerie)) || MODELE_INCONNU,
      maintenant,
      mesIdentites,
      estSuivi: (i: Issue) =>
        !!upnSuivi && motifSuivi(i, estAbonne(i.abonnes, monIdSharePoint, upnSuivi), elementsSuivis) !== null,
    }),
    [modeleParSerie, maintenant, mesIdentites, upnSuivi, monIdSharePoint, elementsSuivis],
  )

  // ─── Du brut à ce qu'on affiche ───
  const filtrees = useMemo(() => filtrer(brutes, reglages, recherche, ctx), [brutes, reglages, recherche, ctx])
  const triees = useMemo(() => trier(filtrees, reglages.tri, ctx), [filtrees, reglages.tri, ctx])
  const entrees = useMemo(
    () => regrouper(triees, reglages.regroupement, repliees),
    [triees, reglages.regroupement, repliees],
  )
  /** Les problèmes visibles, dans l'ordre affiché (fiches sœurs comprises) : la suite de « Problème suivant ». */
  const visibles = useMemo(
    () => entrees.flatMap((e) => (e.type === 'ligne' ? (e.soeurs ?? [e.issue]) : [])),
    [entrees],
  )
  const groupes = useMemo(
    () => entrees.filter((e): e is Extract<EntreeTableau, { type: 'groupe' }> => e.type === 'groupe'),
    [entrees],
  )
  // Fiches sœurs d'un même défaut sur une rangée, sauf regroupé par N° de série
  // (chacune est alors sous son équipement) — même règle que regrouper().
  const fusionne = reglages.regroupement !== 'machine'
  const rangeesDe = useCallback(
    (items: readonly Issue[]): RangeeFusionnee[] => (fusionne ? fusionnerSoeurs(items) : items.map((issue) => ({ issue }))),
    [fusionne],
  )
  /** Au téléphone, les groupes ne se replient pas : tout est dans l'ordre affiché. */
  const suiteTelephone = useMemo(
    () =>
      (reglages.regroupement ? groupes.map((g) => g.items) : [triees]).flatMap((items) =>
        rangeesDe(items).flatMap((r) => r.soeurs ?? [r.issue]),
      ),
    [reglages.regroupement, groupes, triees, rangeesDe],
  )
  /** Nombre de rangées, quand des fiches sœurs en partagent une. */
  const nbRangees = useMemo(
    () => (reglages.regroupement ? groupes.map((g) => g.items) : [triees]).reduce((n, items) => n + rangeesDe(items).length, 0),
    [reglages.regroupement, groupes, triees, rangeesDe],
  )

  const nonAnnules = useMemo(() => brutes.filter(compteCommeProbleme).length, [brutes])
  const total = reglages.filtres.statut?.length ? brutes.length : nonAnnules
  const annulesCaches = useAnnulesCaches(brutes, reglages, recherche, ctx)
  const annulesMasques = reglages.filtres.statut?.length ? 0 : annulesCaches

  // ─── Vues ───
  const vuesFixes = useMemo(
    () => vuesPredefinies({ metiersPerimetre, peutSuivre: interne, sujets: sujetsVues }),
    [metiersPerimetre, interne, sujetsVues],
  )
  const vues = useMemo(() => [...vuesFixes, ...vuesPersonnelles(reglages)], [vuesFixes, reglages])
  const comptesVues = useMemo(
    () => Object.fromEntries(vues.map((v) => [v.id, compterVue(brutes, v, ctx)])),
    [vues, brutes, ctx],
  )
  const vueCourante = vueActive(reglages, vues)

  const toutEstReplie = groupes.length > 0 && groupes.every((g) => repliees.has(g.cle))

  const basculerGroupe = useCallback((cle: string) => {
    setRepliees((prev) => {
      const suite = new Set(prev)
      if (suite.has(cle)) suite.delete(cle)
      else suite.add(cle)
      return suite
    })
  }, [])

  function toutReplier(): void {
    setRepliees(toutEstReplie ? new Set() : new Set(groupes.map((g) => g.cle)))
  }

  function effacerTout(): void {
    setRecherche('')
    modifier((r) => ({ ...r, filtres: {}, mesDeclarations: false, tri: [] }))
  }

  function ouvrirFiltre(col: ColonneId, ancre: HTMLElement): void {
    setFiltre((f) => (f?.col === col && f.ancre === ancre ? null : { col, ancre }))
  }

  const nbFiltres = nbFiltresActifs(reglages)
  const libelleSuite = nbFiltres > 0 || recherche ? 'Problèmes filtrés' : LIBELLE_SUITE_GLOBALE

  const header = (
    <Header
      level={2}
      whiteOnLevel2
      title="Tous les problèmes"
      showBack
      onBack={() => navigate('/')}
      width="large"
      // Sans les problèmes de la page : ils sont filtrés, la recherche
      // globale cherche partout (lus au premier focus).
      centerAction={<RechercheEntete />}
      rightAction={<BoutonRafraichirProblemes />}
    />
  )

  return (
    <AppShell header={header} width="large">
      <div className="tableau-page">
        <BarreTableau
          estBureau={estBureau}
          interne={interne}
          recherche={recherche}
          onRecherche={setRecherche}
          vues={vues}
          comptesVues={comptesVues}
          vueActiveId={vueCourante?.id ?? null}
          vuesFixes={vuesFixes}
          onVue={(v) => modifier((r) => appliquerVue(r, v))}
          onEnregistrerVue={(nom) => modifier((r) => enregistrerVue(r, nom, Date.now().toString(36)))}
          onSupprimerVue={setVueASupprimer}
          epingler={epinglerAccueil.peut ? {
            estEpinglee: (v) => epinglerAccueil.estEpinglee(v.id.replace(/^perso:/, '')),
            onEpingler: (v) => {
              const vue = reglages.vues.find((x) => `perso:${x.id}` === v.id)
              if (!vue) return
              const refus = epinglerAccueil.epingler(vue)
              snackbar.show(refus ?? `« ${vue.nom} » est maintenant une file de votre accueil`, refus ? 'info' : 'success')
            },
          } : undefined}
          reglages={reglages}
          modifier={modifier}
          affiches={filtrees.length}
          nbRangees={nbRangees}
          total={total}
          annulesMasques={annulesMasques}
          nbGroupes={groupes.length}
          toutEstReplie={toutEstReplie}
          onToutReplier={toutReplier}
          onOuvrirFiltre={ouvrirFiltre}
          onOuvrirFeuille={() => setFeuilleOuverte(true)}
          onToutEffacer={effacerTout}
        />

        {error && <div className="tableau-erreur">{error}</div>}

        {vueASupprimer && (
          <Confirmation
            titre={`Supprimer la vue « ${vueASupprimer.libelle} » ?`}
            confirmer="Supprimer la vue"
            annuler="Garder"
            danger
            onConfirmer={() => {
              const id = vueASupprimer.id
              setVueASupprimer(null)
              modifier((r) => supprimerVue(r, id))
            }}
            onAnnuler={() => setVueASupprimer(null)}
          >
            Les problèmes ne sont pas touchés : seule la pastille de la vue disparaît.
          </Confirmation>
        )}

        {loading && brutes.length === 0 && !error && (
          <div className="tableau-attente" aria-busy="true">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse" style={{ width: `${[82, 64, 74, 58, 88, 70, 62, 78][i]}%` }} />
            ))}
          </div>
        )}

        {/* Un seul des deux rendus existe dans le DOM : deux jeux de lignes
            doubleraient les requêtes d'aperçu et les écouteurs. */}
        {!(loading && brutes.length === 0) &&
          !error &&
          (estBureau ? (
            <SuiteListe libelle={libelleSuite} issues={visibles}>
              <TableauProblemes
                entrees={entrees}
                tous={brutes}
                reglages={reglages}
                modifier={modifier}
                ctx={ctx}
                interne={interne}
                repliees={repliees}
                onBasculerGroupe={basculerGroupe}
                filtreOuvert={filtre?.col ?? null}
                onOuvrirFiltre={ouvrirFiltre}
                onEffacerFiltres={effacerTout}
              />
            </SuiteListe>
          ) : (
            <section className="tableau-telephone">
              {triees.length === 0 && (
                <div className="tableau-vide">
                  Aucun résultat pour ces critères.
                  <button type="button" onClick={effacerTout}>
                    Effacer les filtres
                  </button>
                </div>
              )}
              <SuiteListe libelle={libelleSuite} issues={suiteTelephone}>
                {reglages.regroupement ? (
                  groupes.map((g) => (
                    <GroupeTelephone key={g.cle} entree={g} mode={reglages.regroupement!} ctx={ctx} />
                  ))
                ) : (
                  <ListeProblemesTelephone issues={triees} fusionnerSoeurs />
                )}
              </SuiteListe>
            </section>
          ))}
      </div>

      {filtre && estBureau && (
        <PopoverFiltre
          col={filtre.col}
          ancre={filtre.ancre}
          issues={brutes}
          reglages={reglages}
          recherche={recherche}
          ctx={ctx}
          onBasculer={(col, valeur, actif) =>
            modifier((r) => ({ ...r, filtres: basculerValeur(r.filtres, col, valeur, actif) }))
          }
          onEffacer={(cols) =>
            modifier((r) => ({
              ...r,
              filtres: Object.fromEntries(Object.entries(r.filtres).filter(([c]) => !cols.includes(c as ColonneId))),
            }))
          }
          onClose={() => setFiltre(null)}
        />
      )}

      {feuilleOuverte && !estBureau && (
        <FeuilleFiltres
          issues={brutes}
          reglages={reglages}
          recherche={recherche}
          ctx={ctx}
          interne={interne}
          modifier={modifier}
          onClose={() => setFeuilleOuverte(false)}
        />
      )}
    </AppShell>
  )
}

/** Combien d'annulés la recherche et les filtres cachent (pour le dire à l'écran). */
function useAnnulesCaches(
  brutes: readonly Issue[],
  reglages: Parameters<typeof filtrer>[1],
  recherche: string,
  ctx: ContexteTableau,
): number {
  return useMemo(() => {
    const avecAnnules = { ...reglages, filtres: { ...reglages.filtres, statut: ['Annulé' as const] } }
    return filtrer(brutes, avecAnnules, recherche, ctx).length
  }, [brutes, reglages, recherche, ctx])
}

// ─── Regroupement au téléphone ───

function GroupeTelephone({
  entree,
  mode,
  ctx,
}: {
  entree: { cle: string; items: Issue[] }
  mode: Regroupement
  ctx: ContexteTableau
}): JSX.Element {
  const resume = resumerGroupe(entree.items, ctx)
  const premier = entree.items[0]!
  return (
    <section className="tableau-groupe-tel">
      <h3>
        {mode === 'metier' ? <PastilleMetier metier={premier.metier} /> : cleDeGroupe(mode, premier)}
        {mode === 'machine' && <small>{[resume.modele, resume.pn].filter(Boolean).join(' · ')}</small>}
      </h3>
      <p>
        {resume.nbActifs} actif{resume.nbActifs > 1 ? 's' : ''} sur {resume.nbProblemes}
        {resume.nbActifs > 0 && ` · plus ancien : ${resume.plusAncienJours} j`}
      </p>
      <ListeProblemesTelephone issues={entree.items} fusionnerSoeurs={mode !== 'machine'} />
    </section>
  )
}
